'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Spinner } from '@flipster/react/design-system/spinner';
import { Toggle } from '@flipster/react/design-system/toggle';
import ArrowLeftIcon from '@flipster/react/icon/generated/ArrowLeftIcon';
import DocumentSearchIcon from '@flipster/react/icon/generated/DocumentSearchIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';

import { notificationCenterBunja } from './bunja';

export const NotificationSettings = ({ onBackClick }: { onBackClick: () => void }) => {
  const { notificationGroupsAtom, updateNotificationGroupMutationAtom, notificationGroupsQueryAtom } =
    useBunja(notificationCenterBunja);
  const notificationSettings = useAtomValue(notificationGroupsAtom);
  const {
    mutateAsync,
    variables: updateVariables,
    isPending: isUpdatePending,
  } = useAtomValue(updateNotificationGroupMutationAtom);
  const {
    isFetching: isQueryFetching,
    isPending: isQueryPending,
    isError: isQueryError,
  } = useAtomValue(notificationGroupsQueryAtom);
  const handleToggleChange = async (code: string, currentIsSuppressed: boolean) => {
    const newIsSuppressed = !currentIsSuppressed;

    await mutateAsync({
      groupCode: code,
      body: { isSuppressed: newIsSuppressed },
    });
  };

  return (
    <div className={vstack({ h: '70vh', w: '100%', alignItems: 'stretch', overflowY: 'auto', pb: 20 })}>
      <div
        className={hstack({
          w: '100%',
          gap: 8,
          mx: -6,
        })}
      >
        <IconButton
          hierarchy='quaternary'
          size='small'
          color='content.default.level-5'
          icon={<ArrowLeftIcon />}
          onClick={onBackClick}
        />
        <p
          className={css({
            textStyle: 'Title/M',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Autogen_common.r251126.s_Notification_settings' />
        </p>
      </div>
      <p
        className={css({
          textStyle: 'Text/S/Regular',
          color: 'content.default.level-3',
          pt: 20,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderColor: 'border.default.level-2',
          pb: 20,
        })}
      >
        <FormattedMessage id='Autogen_common.r251126.s_Enable_disable_your_push_notification_and_in_app_notification_alerts_Disabling_will_automatically_mark_notifications_as_read' />
      </p>
      {isQueryPending && (
        <div className={center({ h: '100%' })}>
          <Spinner />
        </div>
      )}
      {isQueryError && (
        <div className={center({ h: '100%' })}>
          <div
            className={vstack({
              gap: 3,
              alignItems: 'center',
              textAlign: 'center',
              textStyle: 'Text/M/Regular',
            })}
          >
            <DocumentSearchIcon size={32} />
            <p>
              Can't load notification settings. <br />
              Please try again.
            </p>
          </div>
        </div>
      )}
      {!isQueryPending && !isQueryError && (
        <div className={vstack()}>
          {notificationSettings?.map((setting) => (
            <div key={setting.code} className={vstack({ gap: 8, my: 4, alignItems: 'stretch' })}>
              <div className={hstack({ justify: 'space-between' })}>
                <p
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-5',
                  })}
                >
                  {setting.name}
                </p>
                <Toggle
                  selected={!setting.isSuppressed}
                  onClick={() => handleToggleChange(setting.code, setting.isSuppressed)}
                  disabled={(isUpdatePending || isQueryFetching) && updateVariables?.groupCode === setting.code}
                />
              </div>
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-2',
                })}
              >
                {setting.description}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
